import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveProgram,
  useCreateProgram,
  useProgramsPaginated,
  useUpdateProgram,
} from "@/hooks/usePrograms";
import { useOrganizationsCatalog } from "@/hooks/useOrganizations";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import { Program, ProgramPayload } from "@/domain/programs.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import ProgramFilters, { Filters } from "@/ui/content/programs/Filters";
import ProgramModal from "@/ui/modals/programs/ProgramModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
};

const ProgramsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    program?: Program;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    program?: Program;
  }>({ open: false });

  const {
    programsPaginated,
    isLoadingProgramsPaginated,
    isFetchingProgramsPaginated,
    refetchProgramsPaginated,
  } = useProgramsPaginated({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
  });

  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();
  const archiveProgram = useArchiveProgram();
  const createAuditLog = useCreateAuditlog();

  const { organizationsCatalog } = useOrganizationsCatalog();

  const orgUnitNameById = useMemo(() => {
    const map = new Map<string, string>();
    (organizationsCatalog || []).forEach((org) => {
      if (org._id) map.set(org._id, org.name || org._id);
    });
    return map;
  }, [organizationsCatalog]);

  const isSaving = createProgram.isPending || updateProgram.isPending;

  useEffect(() => {
    setPageTitle("Programs");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.PROGRAMS,
    option: OPTIONS.PROGRAMS_LIST,
    actionCode: ACTION_CODE.WEB_PROG_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of programs",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchProgramsPaginated();
  }, [
    debouncedSearch,
    filters.status,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PROGRAMS,
        option: OPTIONS.PROGRAMS_LIST,
        actionCode: ACTION_CODE.WEB_PROG_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of programs",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, program: undefined });
  };

  const handleOpenEdit = (program: Program) => {
    setModalState({ open: true, program });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, program: undefined });
  };

  const handleSave = async (data: ProgramPayload) => {
    const editing = modalState.program;

    try {
      if (editing) {
        await updateProgram.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.PROGRAMS,
          option: OPTIONS.PROGRAM_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_PROG_EDIT,
          action: "When editing a program",
          detail: `Edited program ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The program has been updated.", "success");
      } else {
        await createProgram.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.PROGRAMS,
          option: OPTIONS.PROGRAM_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_PROG_ADD,
          action: "When creating a program",
          detail: `Created program ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The program has been created.", "success");
      }

      handleCloseModal();
      refetchProgramsPaginated();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.program;
    if (!target) return;

    try {
      await archiveProgram.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PROGRAMS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_PROG_DELETE,
        action: "When deleting a program",
        detail: `Deleted program ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchProgramsPaginated();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "130px",
    },
    {
      id: "eventName",
      isSortable: false,
      disablePadding: false,
      label: "Event",
      align: "left",
      width: "160px",
    },
    {
      id: "orgUnits",
      isSortable: false,
      disablePadding: false,
      label: "Org units",
      align: "left",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!programsPaginated || programsPaginated.list.length === 0) return [];

    return programsPaginated.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, program: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const orgUnitNames = (row.orgUnitId || [])
        .map((id) => orgUnitNameById.get(id) || id)
        .filter(Boolean);

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        eventName: <div>{row.eventName || "-"}</div>,
        orgUnits:
          orgUnitNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {orgUnitNames.map((name, i) => (
                <Chip
                  key={`${row._id}-org-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [programsPaginated, theme, orgUnitNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Programs
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <ProgramFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={programsPaginated?.totalCount}
                  isLoading={
                    isLoadingProgramsPaginated || isFetchingProgramsPaginated
                  }
                  messageEmptyData="No programs to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <ProgramModal
          open={modalState.open}
          onClose={handleCloseModal}
          program={modalState.program}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveProgram.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default ProgramsPage;
